import { useEffect, useRef } from "react";
import { getPusherClient } from "../services/pusherClient";

/**
 * Drop-in replacement for Apollo's useSubscription, backed by Pusher.
 *
 * @param {string} channelName  - e.g. "private-session-abc123"
 * @param {string} eventName    - e.g. "realtime-message-sent"
 * @param {Object} options
 * @param {boolean}  options.skip    - skip subscribing when true
 * @param {Function} options.onData  - called with the event payload
 * @param {Function} options.onError - called on subscription error
 */
export default function usePusherSubscription(channelName, eventName, options = {}) {
  const { skip = false, onData, onError } = options;
  // Keep callback refs stable to avoid re-subscribing on every render
  const onDataRef = useRef(onData);
  const onErrorRef = useRef(onError);
  onDataRef.current = onData;
  onErrorRef.current = onError;

  useEffect(() => {
    if (skip || !channelName || !eventName) return;

    const pusher = getPusherClient();
    const channel = pusher.subscribe(channelName);

    channel.bind("pusher:subscription_error", (err) => {
      console.error(`[Pusher] Subscription error on ${channelName}:`, err);
      if (onErrorRef.current) onErrorRef.current(err);
    });

    channel.bind(eventName, (data) => {
      if (onDataRef.current) onDataRef.current(data);
    });

    return () => {
      channel.unbind(eventName);
      pusher.unsubscribe(channelName);
    };
  }, [channelName, eventName, skip]);
}
